import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { dashFetch } from "@/api/client";
import SiteFavicon from "@/components/SiteFavicon";
import { useDashboard, type Tenant } from "@/context/DashboardContext";
import type { DashUser } from "@/types/dashboard";
import AddSiteWizard from "@/components/AddSiteWizard";
import { fetchAddSiteWizardResumeStep } from "@/utils/addSiteWizardResume";

function isDashAdmin(user: DashUser): boolean {
  if (user.isAdministrator) return true;
  const role = String(user.role || "").toLowerCase();
  return role === "administrator" || role === "admin";
}

function SiteSwitcherItem({
  tenant,
  active,
  onSelect
}: {
  tenant: Tenant;
  active: boolean;
  onSelect: (domain: string) => void;
}) {
  const domain = String(tenant.domain || "");
  const origin = String(tenant.origin || "").trim();
  const pending = String(tenant.status || "active") === "pending";

  return (
    <button
      type="button"
      className={"siteSwitcherItem" + (active ? " active" : "") + (pending ? " is-pending" : "")}
      onClick={() => onSelect(domain)}
    >
      <SiteFavicon domain={domain} />
      <span className="siteSwitcherItemText">
        <span className="siteSwitcherItemDomain">
          {domain}
          {pending ? <span className="siteSwitcherPendingBadge">pending</span> : null}
        </span>
        {origin ? <span className="siteSwitcherItemMeta">{origin}</span> : pending ? <span className="siteSwitcherItemMeta">Awaiting verification</span> : null}
      </span>
      {active ? <span className="siteSwitcherCheck" aria-hidden="true">✓</span> : null}
    </button>
  );
}

export default function SiteSwitcher() {
  const {
    activeSite,
    setActiveSite,
    controlPlaneEnabled,
    tenants,
    loadTenants,
    user
  } = useDashboard();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [verifyWizardOpen, setVerifyWizardOpen] = useState(false);
  const [verifyWizardStep, setVerifyWizardStep] = useState(1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const tenantRows: Tenant[] = tenants.length > 0
    ? tenants
    : activeSite
      ? [{ domain: activeSite }]
      : [];

  const activeTenant = useMemo(
    () => tenantRows.find((t) => String(t.domain || "") === activeSite),
    [tenantRows, activeSite]
  );
  const activePending = String(activeTenant?.status || "active") === "pending";
  const activationPending = activePending && Boolean(activeTenant?.dashboardPreview);

  const showSwitcher = controlPlaneEnabled || tenantRows.length > 0 || Boolean(activeSite);
  const showAdd = controlPlaneEnabled && isDashAdmin(user);

  const openVerifyWizard = useCallback(async () => {
    const domain = String(activeSite || "").trim();
    if (!domain) return;
    setVerifyWizardStep(1);
    setVerifyWizardOpen(true);
    const step = await fetchAddSiteWizardResumeStep(domain);
    setVerifyWizardStep(step);
  }, [activeSite]);

  const filtered = tenantRows.filter((t) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const d = String(t.domain || "").toLowerCase();
    const slug = String(t.slug || t.id || "").toLowerCase();
    const label = String(t.label || t.name || "").toLowerCase();
    return d.includes(q) || slug.includes(q) || label.includes(q);
  });

  const close = useCallback(() => {
    setOpen(false);
    setSearch("");
  }, []);

  const handleSelect = useCallback((domain: string) => {
    if (!domain || domain === activeSite) {
      close();
      return;
    }
    void setActiveSite(domain);
    close();
  }, [activeSite, close, setActiveSite]);

  useEffect(() => {
    if (!open) return;
    void loadTenants();
    const t = window.setTimeout(() => searchRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open, loadTenants]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [close]);

  if (!showSwitcher) return null;

  return (
    <>
      <div className="siteSwitcherRow">
        <div className="siteSwitcherWrap" ref={wrapRef}>
        <button
          type="button"
          className="siteSwitcherBtn"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Switch site"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          <SiteFavicon domain={activeSite || ""} />
          <span className="siteSwitcherCurrent">{activeSite || "Select site"}</span>
          <span className="siteSwitcherChevron" aria-hidden="true">▼</span>
        </button>
        <div
          className={"siteSwitcherPanel" + (open ? " open" : "")}
          role="listbox"
          aria-label="Sites"
          aria-hidden={!open}
        >
          <input
            ref={searchRef}
            type="search"
            className="siteSwitcherSearch"
            placeholder="Search sites…"
            autoComplete="off"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") close(); }}
          />
          <div className="siteSwitcherList">
            {filtered.length === 0 ? (
              <div className="siteSwitcherEmpty">No sites found</div>
            ) : (
              filtered.map((t) => {
                const d = String(t.domain || "");
                return (
                  <SiteSwitcherItem
                    key={d}
                    tenant={t}
                    active={d === activeSite}
                    onSelect={handleSelect}
                  />
                );
              })
            )}
          </div>
          {showAdd ? (
            <>
              <div className="siteSwitcherDivider" />
              <button
                type="button"
                className="siteSwitcherAdd"
                onClick={(e) => {
                  e.stopPropagation();
                  close();
                  setAddOpen(true);
                }}
              >
                + Add website
              </button>
            </>
          ) : null}
        </div>
        </div>
        {activePending && !activationPending ? (
          <div className="sitePendingNotice" role="status">
            <span className="sitePendingNoticeText">This site is not verified yet</span>
            <button
              type="button"
              className="sitePendingNoticeBtn"
              onClick={() => { void openVerifyWizard(); }}
            >
              Open wizard
            </button>
          </div>
        ) : null}
      </div>
      <AddSiteWizard
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onComplete={(domain) => { void setActiveSite(domain); }}
      />
      <AddSiteWizard
        open={verifyWizardOpen}
        onClose={() => setVerifyWizardOpen(false)}
        onComplete={(domain) => {
          void setActiveSite(domain);
          void loadTenants();
        }}
        initialDomain={activeSite || undefined}
        initialStep={verifyWizardStep}
      />
    </>
  );
}
